import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { UserPlus, Users, Phone, Mail, MapPin, Key, Copy, Check, RefreshCw } from "lucide-react";
import SellerLayout, { useSellerSession } from "./layout";
import type { Client } from "@shared/schema";

const clientSchema = z.object({
  name: z.string().min(1, "Namn krävs"),
  address: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email("Ogiltig e-postadress").optional().or(z.literal("")),
  notes: z.string().optional(),
  orgNumber: z.string().optional(),
});

const credSchema = z.object({
  username: z.string().min(3, "Minst 3 tecken"),
  password: z.string().min(10, "Minst 10 tecken"),
});

type ClientFormData = z.infer<typeof clientSchema>;
type CredFormData = z.infer<typeof credSchema>;

function generatePassword(length = 8): string {
  const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";
  const array = new Uint32Array(length);
  crypto.getRandomValues(array);
  let password = "";
  for (let i = 0; i < length; i++) {
    password += chars.charAt(array[i] % chars.length);
  }
  return password;
}

export default function SellerClients() {
  const { data: seller } = useSellerSession();
  const { toast } = useToast();

  const [newOpen, setNewOpen] = useState(false);
  const [credOpen, setCredOpen] = useState(false);
  const [credClient, setCredClient] = useState<Client | null>(null);
  const [savedCreds, setSavedCreds] = useState<{ username: string; password: string; clientName: string } | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const { data: clients, isLoading } = useQuery<Client[]>({ queryKey: ["/api/seller/clients"] });

  const newForm = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: { name: "", address: "", phone: "", email: "", notes: "", orgNumber: "" },
  });

  const credForm = useForm<CredFormData>({
    resolver: zodResolver(credSchema),
    defaultValues: { username: "", password: generatePassword() },
  });

  const createMutation = useMutation({
    mutationFn: (data: ClientFormData) => apiRequest("POST", "/api/seller/clients", data),
    onSuccess: () => {
      toast({ title: "Kund registrerad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/seller/clients"] });
      setNewOpen(false);
      newForm.reset();
    },
    onError: async (error: any) => {
      toast({ title: "Fel", description: error?.message || "Kunde inte registrera kund.", variant: "destructive" });
    },
  });

  const credMutation = useMutation({
    mutationFn: async (data: CredFormData) => {
      await apiRequest("POST", `/api/seller/clients/${credClient!.id}/credentials`, data);
      return data;
    },
    onSuccess: (data) => {
      setSavedCreds({ username: data.username, password: data.password, clientName: credClient!.name });
      queryClient.invalidateQueries({ queryKey: ["/api/seller/clients"] });
      setCredOpen(false);
      setCredClient(null);
    },
    onError: async (error: any) => {
      toast({ title: "Fel", description: error?.message || "Kunde inte spara inloggning.", variant: "destructive" });
    },
  });

  const openCred = async (client: Client) => {
    setCredClient(client);
    try {
      const res = await fetch(`/api/seller/clients/${client.id}/credentials`, { credentials: "include" });
      const data = await res.json();
      credForm.reset({ username: data.username ?? "", password: generatePassword() });
    } catch {
      credForm.reset({ username: "", password: generatePassword() });
    }
    setCredOpen(true);
  };

  const copyField = (value: string, field: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <SellerLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Mina kunder</h1>
            <p className="text-muted-foreground">Kunder du har ansvaret för</p>
          </div>
          {seller?.canRegisterClients && (
            <Button onClick={() => setNewOpen(true)} data-testid="button-add-client">
              <UserPlus className="h-4 w-4 mr-2" />
              Ny kund
            </Button>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Kundlista</CardTitle>
            <CardDescription>{clients?.length ?? 0} kunder tilldelade dig</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : clients && clients.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Namn</TableHead>
                      <TableHead>Org.nr</TableHead>
                      <TableHead>Telefon</TableHead>
                      <TableHead>E-post</TableHead>
                      <TableHead>Adress</TableHead>
                      <TableHead className="text-right">Lösenord</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.map(client => (
                      <TableRow key={client.id} data-testid={`row-client-${client.id}`}>
                        <TableCell className="font-medium">{client.name}</TableCell>
                        <TableCell>{client.orgNumber || "-"}</TableCell>
                        <TableCell>
                          {client.phone ? (
                            <a href={`tel:${client.phone}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                              <Phone className="h-3 w-3" /> {client.phone}
                            </a>
                          ) : "-"}
                        </TableCell>
                        <TableCell>
                          {client.email ? (
                            <a href={`mailto:${client.email}`} className="flex items-center gap-1 text-blue-600 hover:underline">
                              <Mail className="h-3 w-3" /> {client.email}
                            </a>
                          ) : "-"}
                        </TableCell>
                        <TableCell>
                          {client.address ? (
                            <span className="flex items-center gap-1 text-sm">
                              <MapPin className="h-3 w-3 flex-shrink-0" /> {client.address}
                            </span>
                          ) : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => openCred(client)}
                            title="Inloggningsuppgifter"
                            data-testid={`button-cred-client-${client.id}`}
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Inga kunder tilldelade ännu</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* New Client Dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registrera ny kund</DialogTitle>
          </DialogHeader>
          <Form {...newForm}>
            <form onSubmit={newForm.handleSubmit(data => createMutation.mutate(data))} className="space-y-4">
              <FormField control={newForm.control} name="name" render={({ field }) => (
                <FormItem>
                  <FormLabel>Namn *</FormLabel>
                  <FormControl><Input {...field} placeholder="Kundens namn" data-testid="input-client-name" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={newForm.control} name="orgNumber" render={({ field }) => (
                <FormItem>
                  <FormLabel>Org.nr</FormLabel>
                  <FormControl><Input {...field} placeholder="556000-0000" data-testid="input-client-orgnr" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <div className="grid grid-cols-2 gap-3">
                <FormField control={newForm.control} name="phone" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefon</FormLabel>
                    <FormControl><Input {...field} type="tel" placeholder="+46..." data-testid="input-client-phone" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={newForm.control} name="email" render={({ field }) => (
                  <FormItem>
                    <FormLabel>E-post</FormLabel>
                    <FormControl><Input {...field} type="email" data-testid="input-client-email" /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <FormField control={newForm.control} name="address" render={({ field }) => (
                <FormItem>
                  <FormLabel>Adress</FormLabel>
                  <FormControl><Textarea {...field} placeholder="Gatuadress, postnummer, stad" data-testid="input-client-address" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={newForm.control} name="notes" render={({ field }) => (
                <FormItem>
                  <FormLabel>Anteckningar</FormLabel>
                  <FormControl><Textarea {...field} placeholder="Interna anteckningar" data-testid="input-client-notes" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setNewOpen(false)}>Avbryt</Button>
                <Button type="submit" disabled={createMutation.isPending} data-testid="button-submit-client">
                  {createMutation.isPending ? "Sparar..." : "Registrera"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={credOpen} onOpenChange={setCredOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Inloggning – {credClient?.name}</DialogTitle>
            <DialogDescription>Skapa eller återställ kundens inloggning till kundportalen.</DialogDescription>
          </DialogHeader>
          <Form {...credForm}>
            <form onSubmit={credForm.handleSubmit(data => credMutation.mutate(data))} className="space-y-4">
              <FormField control={credForm.control} name="username" render={({ field }) => (
                <FormItem>
                  <FormLabel>Användarnamn</FormLabel>
                  <FormControl><Input {...field} data-testid="input-cred-username" /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={credForm.control} name="password" render={({ field }) => (
                <FormItem>
                  <FormLabel>Lösenord</FormLabel>
                  <div className="flex gap-2">
                    <FormControl><Input {...field} data-testid="input-cred-password" /></FormControl>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => credForm.setValue("password", generatePassword())}
                      title="Generera lösenord"
                      data-testid="button-generate-cred-password"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )} />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCredOpen(false)}>Avbryt</Button>
                <Button type="submit" disabled={credMutation.isPending} data-testid="button-save-cred">
                  {credMutation.isPending ? "Sparar..." : "Spara"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Saved credentials confirmation */}
      {savedCreds && (
        <Dialog open={!!savedCreds} onOpenChange={() => setSavedCreds(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Inloggning sparad</DialogTitle>
              <DialogDescription>
                Spara dessa uppgifter för {savedCreds.clientName}. Lösenordet visas inte igen.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-md bg-muted p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Användarnamn</p>
                    <p className="font-mono text-sm" data-testid="text-saved-username">{savedCreds.username}</p>
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyField(savedCreds.username, "username")} data-testid="button-copy-username">
                    {copiedField === "username" ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Lösenord</p>
                    <p className="font-mono text-sm" data-testid="text-saved-password">{savedCreds.password}</p>
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyField(savedCreds.password, "password")} data-testid="button-copy-password">
                    {copiedField === "password" ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Kunden loggar in via <span className="font-mono">/client</span></p>
            </div>
            <DialogFooter>
              <Button onClick={() => setSavedCreds(null)} data-testid="button-close-saved-creds">Stäng</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </SellerLayout>
  );
}
